import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Position } from '@kemu-io/kemu-core/types';
import { pasteFromClipboardAction } from '../reducers/clipboardReducers';
import {
  copySelectedWidgetsAction,
  removeWidgetsAction,
  selectSelectedWidgets,
  getSelectedWidgetIds,
} from '../logicMapperSlice';
import { selectedBlock, selectVisibleGroup, selectEditingModeWidgetIds } from '../../Workspace/workspaceSlice';

/**
 * Common options for widget clipboard actions.
 * - recipeId: id of the recipe in the pool
 * - thingId: id of the Thing in the recipe
 */
type WidgetClipboardBaseOpts = {
  /** id of the recipe in the pool */
  recipeId?: string;
  /** id of the Thing in the recipe */
  thingId?: string;
};

type CopyOpts = WidgetClipboardBaseOpts & {
  /** id of the group to detach from on copy */
  groupId?: string;
};

type CutOpts = WidgetClipboardBaseOpts & {
  /** id of the group to detach from on copy */
  groupId?: string;
};

type RemoveOpts = WidgetClipboardBaseOpts & {
  /** list of widgets ids to remove. Defaults to current selection */
  widgetIds?: string[];
};

type PasteOpts = WidgetClipboardBaseOpts & {
  /** optional mouse location to paste around */
  mouseLocation?: Position;
};

/**
 * Provides reusable actions for copying, cutting, removing and pasting widgets.
 * Returns memoized arrow functions that read defaults from Redux, while allowing
 * callers to override recipe/thing/selection contexts when needed.
 */
const useWidgetClipboardActions = () => {
  const dispatch = useDispatch();
  const selectedWidgets = useSelector(selectSelectedWidgets);
  const currentBlock = useSelector(selectedBlock);
  const currentGroup = useSelector(selectVisibleGroup);
  const editingModeWidgetIds = useSelector(selectEditingModeWidgetIds);

  /**
   * Returns true when the event target is part of an editable element and
   * the browser should handle native copy/cut/paste behavior instead.
   */
  const shouldIgnoreForEditable = useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target as HTMLElement).closest) { return false; }
    const element = target as HTMLElement;
    const editable = element.closest('input, textarea, [contenteditable="true"]');
    return Boolean(editable) || (element as HTMLElement).isContentEditable === true;
  }, []);

  /** Copies currently selected widgets (or provided context) to the kemu clipboard. */
  const copySelection = useCallback((opts?: CopyOpts) => {
    const recipeId = opts?.recipeId ?? currentBlock?.recipePoolId;
    const thingId = opts?.thingId ?? currentBlock?.recipeId;
    const groupId = opts?.groupId ?? currentGroup?.groupId;

    if (!recipeId || !thingId) { return; }
    if (!selectedWidgets || selectedWidgets.length === 0) { return; }

    dispatch(copySelectedWidgetsAction({ recipeId, thingId, groupId }));
  }, [dispatch, selectedWidgets, currentBlock, currentGroup]);

  /** Removes selected widgets (or provided list) from the current thing. */
  const removeSelection = useCallback((opts?: RemoveOpts) => {
    const recipeId = opts?.recipeId ?? currentBlock?.recipePoolId;
    const thingId = opts?.thingId ?? currentBlock?.recipeId;
    if (!recipeId || !thingId) { return; }

    const widgetsIds = (opts?.widgetIds && opts.widgetIds.length > 0)
      ? opts.widgetIds
      : getSelectedWidgetIds(selectedWidgets);

    if (!widgetsIds || widgetsIds.length === 0) { return; }
    const hasMultipleWidgets = Boolean(selectedWidgets.length);

    dispatch(removeWidgetsAction({
      recipeId,
      thingId,
      widgetsIds,
      clearSelection: hasMultipleWidgets,
      editingModeWidgetIds,
    }));
  }, [dispatch, selectedWidgets, currentBlock, editingModeWidgetIds]);

  /** Copies then removes selected widgets. */
  const cutSelection = useCallback((opts?: CutOpts) => {
    const recipeId = opts?.recipeId ?? currentBlock?.recipePoolId;
    const thingId = opts?.thingId ?? currentBlock?.recipeId;
    const groupId = opts?.groupId ?? currentGroup?.groupId;
    if (!recipeId || !thingId) { return; }
    if (!selectedWidgets || selectedWidgets.length === 0) { return; }

    dispatch(copySelectedWidgetsAction({ recipeId, thingId, groupId }));

    const widgetsIds = getSelectedWidgetIds(selectedWidgets);
    const hasMultipleWidgets = Boolean(selectedWidgets.length);

    dispatch(removeWidgetsAction({
      recipeId,
      thingId,
      widgetsIds,
      clearSelection: hasMultipleWidgets,
      editingModeWidgetIds,
    }));
  }, [dispatch, selectedWidgets, currentBlock, currentGroup, editingModeWidgetIds]);

  /** Pastes widgets from the kemu clipboard/local storage into the current thing. */
  const pasteFromClipboard = useCallback((opts?: PasteOpts) => {
    const recipeId = opts?.recipeId ?? currentBlock?.recipePoolId;
    const thingId = opts?.thingId ?? currentBlock?.recipeId;
    if (!recipeId || !thingId) { return; }

    dispatch(pasteFromClipboardAction({
      recipeId,
      thingId,
      mouseLocation: opts?.mouseLocation,
    }));
  }, [dispatch, currentBlock]);

  return {
    copySelection,
    cutSelection,
    removeSelection,
    pasteFromClipboard,
    shouldIgnoreForEditable,
  };
};

export default useWidgetClipboardActions;


